/**
 * Post Hooks - React Hooks for Post Operations
 *
 * These hooks provide a React-friendly interface to the PostService.
 * They handle React state management while delegating data operations
 * to the service layer.
 *
 * Design Pattern: Hooks consume Service Layer
 * - Hooks manage React state (loading, error, data)
 * - Service handles data access and business logic
 * - Clear separation of concerns
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { DocumentSnapshot } from 'firebase/firestore';
import {
  PostService,
  type PostFilters,
  type CreatePostInput,
  type UpdatePostInput,
} from '../services';
import { AppError } from '../errors';
import type { Post, ContentType, PostStatus, ContentBlock } from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface UsePostsOptions {
  type?: ContentType;
  status?: PostStatus;
  authorId?: string;
  tags?: string[];
  limitCount?: number;
}

interface UsePostsReturn {
  posts: Post[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface UsePostReturn {
  post: Post | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface UsePaginatedPostsOptions {
  type?: ContentType;
  status?: PostStatus;
  authorId?: string;
  tags?: string[];
  pageSize?: number;
}

interface UsePaginatedPostsReturn {
  posts: Post[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

// =============================================================================
// ERROR HANDLING UTILITY
// =============================================================================

/**
 * Converts an error to a user-friendly Korean message.
 */
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof AppError) {
    return error.message;
  }
  console.error('Unexpected error:', error);
  return defaultMessage;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook for fetching multiple posts with optional filtering.
 * Uses PostService for data operations.
 */
export function usePosts(options: UsePostsOptions = {}): UsePostsReturn {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters: PostFilters = {
        type: options.type,
        status: options.status,
        authorId: options.authorId,
        tags: options.tags,
      };

      const fetchedPosts = await PostService.findMany(filters, {
        limit: options.limitCount,
        orderByField: 'updatedAt',
        orderDirection: 'desc',
      });

      setPosts(fetchedPosts);
    } catch (err) {
      setError(getErrorMessage(err, '게시물을 불러오는데 실패했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [
    options.type,
    options.status,
    options.authorId,
    options.tags?.join(','),
    options.limitCount,
  ]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return { posts, loading, error, refresh: fetchPosts };
}

/**
 * Hook for fetching a single post by ID.
 * Uses PostService for data operations.
 */
export function usePost(postId: string | undefined): UsePostReturn {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPost = useCallback(async () => {
    if (!postId) {
      setPost(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fetchedPost = await PostService.findById(postId);
      setPost(fetchedPost);
    } catch (err) {
      setError(getErrorMessage(err, '게시물을 불러오는데 실패했습니다.'));
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  return { post, loading, error, refresh: fetchPost };
}

interface UsePostBySlugOptions {
  /** Optional status filter (required for unauthenticated/public access) */
  status?: PostStatus;
}

/**
 * Hook for fetching a single post by slug.
 * Uses PostService for data operations.
 * @param slug - The post slug to search for
 * @param options - Optional configuration (e.g., status filter for public access)
 */
export function usePostBySlug(
  slug: string | undefined,
  options: UsePostBySlugOptions = {}
): UsePostReturn {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPost = useCallback(async () => {
    if (!slug) {
      setPost(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fetchedPost = await PostService.findBySlug(slug, options.status);
      setPost(fetchedPost);
    } catch (err) {
      setError(getErrorMessage(err, '게시물을 불러오는데 실패했습니다.'));
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [slug, options.status]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  return { post, loading, error, refresh: fetchPost };
}

// =============================================================================
// MUTATION TYPES (for backwards compatibility)
// =============================================================================

export interface CreatePostData {
  type: ContentType;
  title: string;
  slug: string;
  description: string;
  content: ContentBlock[];
  thumbnail: string;
  thumbnailMediaId?: string;
  thumbnailSrcSet?: string;
  tags: string[];
  client?: string;
  location?: string;
  date?: Date;
  status: PostStatus;
  authorId: string;
}

export interface UpdatePostData extends Partial<Omit<CreatePostData, 'authorId'>> {
  publishedAt?: Date | null;
}

/**
 * Hook for post mutations (create, update, delete, publish, unpublish).
 * Uses PostService for all data operations.
 */
export function usePostMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPost = async (data: CreatePostData): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const input: CreatePostInput = {
        type: data.type,
        title: data.title,
        slug: data.slug,
        description: data.description,
        content: data.content,
        thumbnail: data.thumbnail,
        thumbnailMediaId: data.thumbnailMediaId,
        thumbnailSrcSet: data.thumbnailSrcSet,
        tags: data.tags,
        client: data.client,
        location: data.location,
        date: data.date,
        status: data.status,
        authorId: data.authorId,
      };

      return await PostService.create(input);
    } catch (err) {
      const message = getErrorMessage(err, '게시물 생성에 실패했습니다.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePost = async (postId: string, data: UpdatePostData): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const input: UpdatePostInput = {
        title: data.title,
        slug: data.slug,
        description: data.description,
        content: data.content,
        thumbnail: data.thumbnail,
        thumbnailMediaId: data.thumbnailMediaId,
        thumbnailSrcSet: data.thumbnailSrcSet,
        tags: data.tags,
        client: data.client,
        location: data.location,
        date: data.date,
        status: data.status,
        publishedAt: data.publishedAt,
      };

      await PostService.update(postId, input);
    } catch (err) {
      const message = getErrorMessage(err, '게시물 수정에 실패했습니다.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (postId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await PostService.delete(postId);
    } catch (err) {
      const message = getErrorMessage(err, '게시물 삭제에 실패했습니다.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const publishPost = async (postId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await PostService.publish(postId);
    } catch (err) {
      const message = getErrorMessage(err, '게시물 발행에 실패했습니다.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const unpublishPost = async (postId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await PostService.unpublish(postId);
    } catch (err) {
      const message = getErrorMessage(err, '게시물 발행 취소에 실패했습니다.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createPost,
    updatePost,
    deletePost,
    publishPost,
    unpublishPost,
    loading,
    error,
  };
}

/**
 * Hook for paginated posts with load more functionality.
 * Uses PostService for data operations.
 */
export function usePaginatedPosts(
  options: UsePaginatedPostsOptions = {}
): UsePaginatedPostsReturn {
  const { pageSize = 20 } = options;
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const lastDocRef = useRef<DocumentSnapshot | null>(null);

  const filters: PostFilters = {
    type: options.type,
    status: options.status,
    authorId: options.authorId,
    tags: options.tags,
  };

  const fetchTotalCount = useCallback(async () => {
    try {
      const count = await PostService.count(filters);
      setTotalCount(count);
    } catch (err) {
      console.error('Error fetching count:', err);
    }
  }, [options.type, options.status, options.authorId, options.tags?.join(',')]);

  const fetchPosts = useCallback(
    async (isLoadMore = false) => {
      setLoading(true);
      setError(null);

      try {
        const result = await PostService.findManyPaginated(filters, {
          limit: pageSize,
          orderByField: 'updatedAt',
          orderDirection: 'desc',
          cursor: isLoadMore ? lastDocRef.current : null,
        });

        if (isLoadMore) {
          setPosts((prev) => [...prev, ...result.data]);
        } else {
          setPosts(result.data);
        }

        lastDocRef.current = result.lastDoc;
        setHasMore(result.hasMore);
      } catch (err) {
        setError(getErrorMessage(err, '게시물을 불러오는데 실패했습니다.'));
      } finally {
        setLoading(false);
      }
    },
    [
      options.type,
      options.status,
      options.authorId,
      options.tags?.join(','),
      pageSize,
    ]
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchPosts(true);
  }, [fetchPosts, hasMore, loading]);

  const refresh = useCallback(async () => {
    lastDocRef.current = null;
    setHasMore(true);
    await fetchTotalCount();
    await fetchPosts(false);
  }, [fetchTotalCount, fetchPosts]);

  useEffect(() => {
    refresh();
  }, [options.type, options.status, options.authorId, options.tags?.join(',')]);

  return { posts, loading, error, hasMore, totalCount, loadMore, refresh };
}
